"use client";

import { Image as ImageType } from "@/types/Image";
import Image from "next/image";
import { useState } from "react";
import AlbumCarouselModal from "../AlbumModal/AlbumCarouselModal";
import "./index.scss";

const renderImageColumn = (
  images: ImageType[],
  columnShrinkBy: number,
  isAbsolute: boolean,
  sizes: string,
  showImage: (images: ImageType[], imageIndex: number) => void,
  imageIndexPadding: number,
  allImages: ImageType[]
) => {
  const adjustmentAmount = Math.floor(columnShrinkBy / images.length);

  return (
    <div className={`column ${isAbsolute ? "absolute" : ""}`}>
      <div className="column-inner-wrapper">
        {images.map((image, imageIndex) => {
          const normalizedWidth = 100;
          const normalizedHeight = Math.floor(
            (image.height / image.width) * normalizedWidth
          );

          const shrinkBy =
            imageIndex === images.length - 1
              ? columnShrinkBy
              : adjustmentAmount;
          const adjustedHeight = normalizedHeight - shrinkBy;

          columnShrinkBy = columnShrinkBy - shrinkBy;

          return (
            <div
              className="image-container"
              style={{
                paddingTop: `${(adjustedHeight / normalizedWidth) * 100}%`,
              }}
              data-shrink-by={shrinkBy}
              key={`${image.src}-${imageIndex}`}
            >
              <a
                onClick={() =>
                  showImage(allImages, imageIndex + imageIndexPadding)
                }
              >
                <Image
                  src={image.src}
                  alt={image.alt}
                  fill={true}
                  sizes={sizes}
                  style={{ objectFit: "cover" }}
                />
              </a>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const renderTwoColumns = (
  images: ImageType[],
  showImage: (images: ImageType[], imageIndex: number) => void
) => {
  const column1Images: ImageType[] = [];
  const column2Images: ImageType[] = [];

  let column1Height = 0;
  let column2Height = 0;

  images.forEach((image) => {
    const normalizedWidth = 100;
    const normalizedHeight = Math.floor(
      (image.height / image.width) * normalizedWidth
    );

    if (column1Height <= column2Height) {
      column1Images.push(image);
      column1Height += normalizedHeight;
    } else {
      column2Images.push(image);
      column2Height += normalizedHeight;
    }
  });

  const allImages = [...column1Images, ...column2Images];

  let column1ShrinkBy = 0;
  let column2ShrinkBy = 0;
  if (column1Height < column2Height) {
    column2ShrinkBy = column2Height - column1Height;
  } else {
    column1ShrinkBy = column1Height - column2Height;
  }

  const column1Gaps = column1Images.length - 1;
  const column2Gaps = column2Images.length - 1;
  const minNumberOfGaps = Math.min(column1Gaps, column2Gaps);

  const isColumn1PositionAbsolute = column1Gaps > minNumberOfGaps;
  const isColumn2PositionAbsolute = column2Gaps > minNumberOfGaps;
  return (
    <div className="column-layout two-columns">
      {renderImageColumn(
        column1Images,
        column1ShrinkBy,
        isColumn1PositionAbsolute,
        "50vw",
        showImage,
        0,
        allImages
      )}
      {renderImageColumn(
        column2Images,
        column2ShrinkBy,
        isColumn2PositionAbsolute,
        "50vw",
        showImage,
        column1Images.length,
        allImages
      )}
    </div>
  );
};

const renderThreeColumns = (
  images: ImageType[],
  showImage: (images: ImageType[], imageIndex: number) => void
) => {
  const column1Images: ImageType[] = [];
  const column2Images: ImageType[] = [];
  const column3Images: ImageType[] = [];

  let column1Height = 0;
  let column2Height = 0;
  let column3Height = 0;

  images.forEach((image) => {
    const normalizedWidth = 100;
    const normalizedHeight = Math.floor(
      (image.height / image.width) * normalizedWidth
    );

    if (column1Height <= column2Height && column1Height <= column3Height) {
      column1Images.push(image);
      column1Height += normalizedHeight;
    } else if (column2Height <= column3Height) {
      column2Images.push(image);
      column2Height += normalizedHeight;
    } else {
      column3Images.push(image);
      column3Height += normalizedHeight;
    }
  });

  const allImages = [...column1Images, ...column2Images, ...column3Images];

  let column1ShrinkBy = 0;
  let column2ShrinkBy = 0;
  let column3ShrinkBy = 0;
  if (column1Height < column2Height && column1Height < column3Height) {
    column2ShrinkBy = column2Height - column1Height;
    column3ShrinkBy = column3Height - column1Height;
  } else if (column2Height < column1Height && column2Height < column3Height) {
    column1ShrinkBy = column1Height - column2Height;
    column3ShrinkBy = column3Height - column2Height;
  } else {
    column1ShrinkBy = column1Height - column3Height;
    column2ShrinkBy = column2Height - column3Height;
  }

  const column1Gaps = column1Images.length - 1;
  const column2Gaps = column2Images.length - 1;
  const column3Gaps = column3Images.length - 1;
  const minNumberOfGaps = Math.min(column1Gaps, column2Gaps, column3Gaps);

  const isColumn1PositionAbsolute = column1Gaps > minNumberOfGaps;
  const isColumn2PositionAbsolute = column2Gaps > minNumberOfGaps;
  const isColumn3PositionAbsolute = column3Gaps > minNumberOfGaps;
  return (
    <div className="column-layout three-columns">
      {renderImageColumn(
        column1Images,
        column1ShrinkBy,
        isColumn1PositionAbsolute,
        "(max-width: 1170px) 33vw, 390px",
        showImage,
        0,
        allImages
      )}
      {renderImageColumn(
        column2Images,
        column2ShrinkBy,
        isColumn2PositionAbsolute,
        "(max-width: 1170px) 33vw, 390px",
        showImage,
        column1Images.length,
        allImages
      )}
      {renderImageColumn(
        column3Images,
        column3ShrinkBy,
        isColumn3PositionAbsolute,
        "(max-width: 1170px) 33vw, 390px",
        showImage,
        column1Images.length + column2Images.length,
        allImages
      )}
    </div>
  );
};

const renderFourColumns = (
  images: ImageType[],
  showImage: (images: ImageType[], imageIndex: number) => void
) => {
  const column1Images: ImageType[] = [];
  const column2Images: ImageType[] = [];
  const column3Images: ImageType[] = [];
  const column4Images: ImageType[] = [];

  let column1Height = 0;
  let column2Height = 0;
  let column3Height = 0;
  let column4Height = 0;

  images.forEach((image) => {
    const normalizedWidth = 100;
    const normalizedHeight = Math.floor(
      (image.height / image.width) * normalizedWidth
    );

    if (
      column1Height <= column2Height &&
      column1Height <= column3Height &&
      column1Height <= column4Height
    ) {
      column1Images.push(image);
      column1Height += normalizedHeight;
    } else if (
      column2Height <= column3Height &&
      column2Height <= column4Height
    ) {
      column2Images.push(image);
      column2Height += normalizedHeight;
    } else if (column3Height <= column4Height) {
      column3Images.push(image);
      column3Height += normalizedHeight;
    } else {
      column4Images.push(image);
      column4Height += normalizedHeight;
    }
  });

  const allImages = [
    ...column1Images,
    ...column2Images,
    ...column3Images,
    ...column4Images,
  ];

  let column1ShrinkBy = 0;
  let column2ShrinkBy = 0;
  let column3ShrinkBy = 0;
  let column4ShrinkBy = 0;
  if (
    column1Height < column2Height &&
    column1Height < column3Height &&
    column1Height < column4Height
  ) {
    column2ShrinkBy = column2Height - column1Height;
    column3ShrinkBy = column3Height - column1Height;
    column4ShrinkBy = column4ShrinkBy - column1Height;
  } else if (
    column2Height < column1Height &&
    column2Height < column3Height &&
    column2Height < column4Height
  ) {
    column1ShrinkBy = column1Height - column2Height;
    column3ShrinkBy = column3Height - column2Height;
    column4ShrinkBy = column4Height - column2Height;
  } else if (
    column3Height < column1Height &&
    column3Height < column2Height &&
    column3Height < column4Height
  ) {
    column1ShrinkBy = column1Height - column3Height;
    column2ShrinkBy = column2Height - column3Height;
    column4ShrinkBy = column4Height - column3Height;
  } else {
    column1ShrinkBy = column1Height - column4Height;
    column2ShrinkBy = column2Height - column4Height;
    column3ShrinkBy = column3Height - column4Height;
  }

  const column1Gaps = column1Images.length - 1;
  const column2Gaps = column2Images.length - 1;
  const column3Gaps = column3Images.length - 1;
  const column4Gaps = column4Images.length - 1;
  const minNumberOfGaps = Math.min(
    column1Gaps,
    column2Gaps,
    column3Gaps,
    column4Gaps
  );

  const isColumn1PositionAbsolute = column1Gaps > minNumberOfGaps;
  const isColumn2PositionAbsolute = column2Gaps > minNumberOfGaps;
  const isColumn3PositionAbsolute = column3Gaps > minNumberOfGaps;
  const isColumn4PositionAbsolute = column4Gaps > minNumberOfGaps;
  return (
    <div className="column-layout four-columns">
      {renderImageColumn(
        column1Images,
        column1ShrinkBy,
        isColumn1PositionAbsolute,
        "(max-width: 1170px) 25vw, 300px",
        showImage,
        0,
        allImages
      )}
      {renderImageColumn(
        column2Images,
        column2ShrinkBy,
        isColumn2PositionAbsolute,
        "(max-width: 1170px) 25vw, 300px",
        showImage,
        column1Images.length,
        allImages
      )}
      {renderImageColumn(
        column3Images,
        column3ShrinkBy,
        isColumn3PositionAbsolute,
        "(max-width: 1170px) 25vw, 300px",
        showImage,
        column1Images.length + column2Images.length,
        allImages
      )}
      {renderImageColumn(
        column4Images,
        column4ShrinkBy,
        isColumn4PositionAbsolute,
        "(max-width: 1170px) 25vw, 300px",
        showImage,
        column1Images.length + column2Images.length + column3Images.length,
        allImages
      )}
    </div>
  );
};

const ImageGridMosaic = ({ images }: { images: ImageType[] }) => {
  const [showAlbumCarouselModal, setShowAlbumCarouselModal] = useState(false);
  const [albumCarouselModalImages, setAlbumCarouselModalImages] =
    useState(images);
  const [albumCarouselModalImageIndex, setAlbumCarouselModalImageIndex] =
    useState(0);

  const showImage = (images: ImageType[], imageIndex: number) => {
    setAlbumCarouselModalImages(images);
    setAlbumCarouselModalImageIndex(imageIndex);
    setShowAlbumCarouselModal(true);
  };

  return (
    <div className="component image-grid-mosaic">
      <div className="image-grid-mosaic-inner-wrapper">
        {renderTwoColumns(images, showImage)}
        {renderThreeColumns(images, showImage)}
        {renderFourColumns(images, showImage)}
      </div>
      {showAlbumCarouselModal && (
        <AlbumCarouselModal
          images={albumCarouselModalImages}
          initialImageIndex={albumCarouselModalImageIndex}
          onClose={() => setShowAlbumCarouselModal(false)}
        />
      )}
    </div>
  );
};

export default ImageGridMosaic;
